import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Eye,
  ExternalLink,
  Link as LinkIcon,
  Lock,
  RefreshCw,
  Trash2,
  Unlink,
  X,
} from 'lucide-react';
import {
  formatBacklogProjectLabel,
  isWbsItemBacklogLinked,
  isWbsItemShadow,
  primaryBacklogIssueKey,
  readWbsItemSystems,
  type AssigneeDoc,
  type BacklogCacheDoc,
  type PhaseBacklogCategoryLink,
  type PhaseBacklogMilestoneLink,
  type PhaseBacklogParentIssueLink,
  type ProjectDoc,
  type WbsItemDoc,
  type WbsItemStatus,
  type WbsProgressMode,
} from '@mct/shared';
import { buildBacklogIssueUrl } from '../../lib/backlog';
import { AssigneeChipPicker } from '../common/AssigneeChipPicker';
import { AreaMultiPicker } from '../common/AreaMultiPicker';
import { BacklogLinkSection } from '../common/BacklogLinkSection';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';
import { PhaseBacklogLinkDialog } from './PhaseBacklogLinkDialog';
import { PhaseBacklogParentIssueLinkDialog } from './PhaseBacklogParentIssueLinkDialog';
import { PhaseBacklogCategoryLinkDialog } from './PhaseBacklogCategoryLinkDialog';
import {
  syncBacklogMilestoneNow,
  unlinkPhaseFromBacklogMilestone,
} from '../../lib/backlogMilestoneLink';
import {
  syncBacklogParentIssueNow,
  unlinkPhaseFromBacklogParentIssue,
} from '../../lib/backlogParentIssueLink';
import {
  syncBacklogCategoryNow,
  unlinkPhaseFromBacklogCategory,
} from '../../lib/backlogCategoryLink';
import { Input, Textarea, Label } from '../ui/Input';
import { cn } from '../../lib/utils';
import { getDb } from '../../lib/firebase';
import {
  updateWbsItem,
  updateWbsItemKind,
  deleteWbsItem,
  WBS_COMPLETION_CRITERIA_MAX_LENGTH,
} from '../../lib/wbs';
import { KindChip } from './KindChip';
import { BacklogImageContent } from './BacklogImageContent';
import { BacklogCommentsSection } from './BacklogCommentsSection';
import { tsToDate } from '../../lib/wbsGantt';
import { formatRelativeFromNow } from '../../lib/relativeTime';
import { areaBadgeClasses, type AreaColorIndexMap } from '../../lib/areaColor';
import { DateInput } from '../ui/DateInput';
import {
  validatePlannedEnd,
  validatePlannedStart,
} from '../../lib/wbsConstraints';
import { useBacklogCache } from '../../hooks/useFirestoreData';
import {
  AutosaveIndicator,
  useAutosaveStatus,
} from '../../hooks/useAutosaveStatus';
import { DependencyChips } from './DependencyChips';
import { RollupChildren } from './RollupChildren';
import { SuccessorChips } from './SuccessorChips';

const STATUS_OPTIONS: { value: WbsItemStatus; label: string }[] = [
  { value: 'not_started', label: '未着手' },
  { value: 'in_progress', label: '進行中' },
  { value: 'done', label: '完了' },
  { value: 'blocked', label: 'ブロック' },
  { value: 'cancelled', label: '中止' },
];

/** Debounce window for text-field autosave (説明 / 名称 inline editor). */
const AUTOSAVE_DEBOUNCE_MS = 400;

/** Tooltip surfaced on disabled 予定開始 / 予定終了 inputs (was an
 *  always-on banner before the autosave refactor). */
const ROLLUP_TOOLTIP = '子行から自動集計 — 予定日は直接編集できません';

function toDateInput(d: Date | null): string {
  if (!d) return '';
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function BacklogAutoBadge({
  updatedAt,
}: {
  updatedAt: Date | null;
}): JSX.Element | null {
  if (!updatedAt) return null;
  const label = formatRelativeFromNow(updatedAt);
  return (
    <span
      data-testid="wbs-backlog-auto-badge"
      title="この値は Backlog 同期で更新されました。手動編集で上書きできます。"
      // `whitespace-nowrap` keeps the "Backlog 同期 X 時間前" label on a
      // single line even when the chip sits in a tight flex row on
      // mobile — wrapping inside a 4×height chip looked broken.
      className="ml-1.5 inline-flex shrink-0 items-center gap-0.5 whitespace-nowrap rounded bg-sky-50 px-1 py-[1px] text-[10px] font-medium text-sky-700"
    >
      <span aria-hidden>⟳</span>
      <span>Backlog 同期 {label}</span>
    </span>
  );
}

/** Tooltip shown on fields locked because the row is mastered by Backlog. */
const BACKLOG_LOCK_TOOLTIP =
  'Backlog 連携中 — このフィールドは Backlog がマスターです。編集は Backlog 側で行ってください。';

/**
 * Small "🔗 Backlog 由来" chip plus a jump-to-Backlog button. Rendered in
 * the panel header when the row mirrors a Backlog issue, so the user sees
 * the provenance and can open the source issue in one click.
 */
function BacklogLinkedBadge({
  issueKey,
  spaceUrl,
}: {
  issueKey: string;
  spaceUrl: string | null;
}): JSX.Element {
  const url = buildBacklogIssueUrl(spaceUrl, issueKey);
  return (
    <span
      data-testid="wbs-backlog-linked-badge"
      className="inline-flex items-center gap-1 rounded bg-indigo-50 px-1.5 py-[2px] text-[10px] font-medium text-indigo-700"
      title={BACKLOG_LOCK_TOOLTIP}
    >
      <LinkIcon className="h-3 w-3" aria-hidden />
      <span>Backlog 由来 ({issueKey})</span>
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center text-indigo-600 hover:text-indigo-800"
          aria-label={`Backlog ${issueKey} を開く`}
          data-testid="wbs-backlog-open-link"
        >
          <ExternalLink className="h-3 w-3" />
        </a>
      ) : null}
    </span>
  );
}

/**
 * Prominent banner shown at the top of the detail body when the
 * auto-relinker could not find a phase matching the row's Backlog source
 * milestone (`backlogMilestoneMismatch` is set). The cron sync re-checks
 * on its next pass, so there's no manual clear action — the banner just
 * tells the user the linked milestone drifted away from the WBS tree.
 */
function BacklogMilestoneMismatchBanner({
  mismatch,
}: {
  mismatch: WbsItemDoc['backlogMilestoneMismatch'];
}): JSX.Element | null {
  if (!mismatch) return null;
  const names = mismatch.sourceMilestoneNames ?? [];
  const joined = names.length > 0 ? names.join('、') : '(不明)';
  return (
    <div
      data-testid="wbs-backlog-milestone-mismatch-banner"
      role="alert"
      className="mb-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-900"
    >
      <span aria-hidden className="mt-0.5 text-sm leading-none">
        ⚠
      </span>
      <div>
        <p className="font-semibold">連携先マイルストン不整合発生中</p>
        <p className="mt-0.5">
          Backlog のマイルストン（{joined}）に対応する phase
          が見つかりませんでした。対応する phase
          を作成すると、次回の同期で自動的に移動します。
        </p>
      </div>
    </div>
  );
}

/**
 * Read-only "Backlog 由来" metadata: the source milestone / category
 * names the linked issue currently belongs to. Backlog masters these, so
 * they're display-only. Hidden entirely for manual (non-Backlog) rows or
 * rows that carry neither name list.
 */
function BacklogSourceMetaSection({
  item,
}: {
  item: WbsItemDoc;
}): JSX.Element | null {
  if (!isWbsItemBacklogLinked(item)) return null;
  const milestones = item.backlogSourceMilestoneNames ?? [];
  const categories = item.backlogSourceCategoryNames ?? [];
  if (milestones.length === 0 && categories.length === 0) return null;
  return (
    <div
      className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px]"
      data-testid="wbs-backlog-source-meta"
    >
      {milestones.length > 0 ? (
        <span
          className="inline-flex items-center gap-1"
          data-testid="wbs-backlog-source-milestone"
        >
          <span className="text-slate-500">マイルストン</span>
          <span className="font-medium text-slate-700">
            {milestones.join('、')}
          </span>
          <BacklogLockMark />
        </span>
      ) : null}
      {categories.length > 0 ? (
        <span
          className="inline-flex items-center gap-1"
          data-testid="wbs-backlog-source-category"
        >
          <span className="text-slate-500">カテゴリ</span>
          <span className="font-medium text-slate-700">
            {categories.join('、')}
          </span>
          <BacklogLockMark />
        </span>
      ) : null}
    </div>
  );
}

/**
 * Read-only "Backlog 由来" provenance chips: which Backlog milestone /
 * parent issue / category created this row. Unlike the list (which only
 * surfaces the 外れ warning), the detail panel shows the full provenance —
 * all origins the row carries. A row whose source issues have all left
 * (empty `backlogIssueKeys`) is "外れ" and tinted light red.
 */
function BacklogOriginSection({
  item,
}: {
  item: WbsItemDoc;
}): JSX.Element | null {
  const origins: { key: string; label: string; value: string }[] = [];
  if (item.backlogMilestoneOrigin) {
    origins.push({
      key: 'milestone',
      label: 'milestone',
      value: item.backlogMilestoneOrigin.milestoneName,
    });
  }
  if (item.backlogParentIssueOrigin) {
    origins.push({
      key: 'parent',
      label: 'parent issue',
      value: item.backlogParentIssueOrigin.parentIssueKey,
    });
  }
  if (item.backlogCategoryOrigin) {
    origins.push({
      key: 'category',
      label: 'category',
      value: item.backlogCategoryOrigin.categoryName,
    });
  }
  if (origins.length === 0) return null;
  const orphaned = (item.backlogIssueKeys ?? []).length === 0;
  return (
    <div
      className="mb-3 flex flex-wrap items-center gap-1.5 text-[11px]"
      data-testid="wbs-backlog-origin"
    >
      {origins.map((o) => (
        <span
          key={o.key}
          data-testid={`wbs-backlog-origin-${o.key}`}
          className={cn(
            'inline-flex items-center gap-1 rounded border px-1.5 py-[2px] font-medium',
            orphaned
              ? 'border-red-200 bg-red-50 text-red-700'
              : 'border-slate-200 bg-slate-50 text-slate-700',
          )}
        >
          {orphaned ? <span aria-hidden>⚠</span> : null}
          <span>
            Backlog 由来 ({o.label}): {o.value}
          </span>
        </span>
      ))}
    </div>
  );
}

/** Inline "🔒 Backlog 管理" marker rendered next to a locked field label. */
function BacklogLockMark(): JSX.Element {
  return (
    <span
      data-testid="wbs-backlog-lock-mark"
      className="ml-1 inline-flex items-center gap-0.5 text-[10px] text-indigo-500"
      title={BACKLOG_LOCK_TOOLTIP}
    >
      <Lock className="h-3 w-3" aria-hidden />
      <span>Backlog 管理</span>
    </span>
  );
}

function parseDateInput(v: string): Date | null {
  if (!v) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
}

interface Props {
  project: ProjectDoc;
  item: WbsItemDoc;
  allItems: readonly WbsItemDoc[];
  /**
   * Project-wide assignee list (same source as the migration-task DAG
   * view and the Settings page — `useAssignees`). Threaded through so
   * the panel never has to query Firestore on its own.
   */
  assignees: readonly AssigneeDoc[];
  /** Project area catalog (label list + colors) shared with the tree cells. */
  areaOptions?: readonly string[];
  areaColorIndices?: AreaColorIndexMap;
  /** Project vendor catalog (label list + colors). */
  vendorOptions?: readonly string[];
  vendorColorIndices?: AreaColorIndexMap;
  hasChildren: boolean;
  descendantIds: readonly string[];
  onClose: () => void;
  /**
   * Focus another row from inside the panel (used by dependency chips so
   * clicking a chip jumps to that predecessor in the tree). Optional —
   * callers that don't supply it disable the chip-click navigation.
   */
  onSelectItem?: (itemId: string) => void;
}

export function WbsItemDetailPanel({
  project,
  item,
  allItems,
  assignees,
  areaOptions,
  areaColorIndices,
  vendorOptions,
  vendorColorIndices,
  hasChildren,
  descendantIds,
  onClose,
  onSelectItem,
}: Props) {
  const pid = project.projectId;
  const { data: backlogCache } = useBacklogCache(pid);
  const { status: saveStatus, run: runSave } = useAutosaveStatus();
  // A row that mirrors a Backlog issue is Backlog-mastered: the synced
  // fields (title / 説明 / status / 予定日 / 担当 / 領域) become read-only
  // so Backlog stays the single source of truth.
  const backlogLocked = isWbsItemBacklogLinked(item);
  const backlogIssueKey = primaryBacklogIssueKey(item);
  const primaryBacklogCacheEntry = useMemo<BacklogCacheDoc | null>(() => {
    if (!backlogIssueKey) return null;
    return backlogCache.find((b) => b.issueKey === backlogIssueKey) ?? null;
  }, [backlogCache, backlogIssueKey]);
  // Lock-banner sync time. Prefer the project-level `backlogCache`
  // (authoritative — written by the sync worker), fall back to
  // `item.backlogAutoUpdatedAt` mirrored on the row so the banner
  // matches `BacklogAutoBadge` even when the cache entry hasn't
  // streamed in yet.
  const lockBannerSyncDate = useMemo(() => {
    const fromCache = tsToDate(primaryBacklogCacheEntry?.lastSyncedAt ?? null);
    if (fromCache) return fromCache;
    return tsToDate(item.backlogAutoUpdatedAt ?? null);
  }, [primaryBacklogCacheEntry, item.backlogAutoUpdatedAt]);

  // Shadow (参照) rows mirror an original task in a different phase. Resolve
  // the original + its phase so the banner can name + link it.
  const isShadow = isWbsItemShadow(item);
  const shadowOriginal = isShadow
    ? allItems.find((it) => it.itemId === item.shadowOf) ?? null
    : null;
  const shadowOriginalPhase = shadowOriginal
    ? allItems.find((it) => it.itemId === shadowOriginal.parentItemId) ?? null
    : null;

  return (
    <aside
      data-testid="wbs-detail-panel"
      className={
        // Phase 1 responsive: full-screen overlay below `lg`, sticky
        // 620px sidebar at `lg+`. The parent shell in WbsPage layers a
        // dim background on mobile; this element owns its own panel
        // chrome for both modes.
        // `overflow-hidden` on the aside prevents children (notably the
        // long Backlog screenshot in the description / comments) from
        // pushing the panel into horizontal scroll on phones — the body
        // div below owns vertical scroll instead.
        'relative z-50 ml-auto flex h-screen w-full max-w-full shrink-0 flex-col overflow-hidden border-l border-slate-200 bg-white lg:z-auto lg:h-auto lg:w-[620px] lg:max-w-none'
      }
    >
      <header
        className="flex items-start justify-between gap-2 border-b border-slate-200 px-4 py-2 pt-[max(0.5rem,env(safe-area-inset-top))]"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <KindChip
              item={item}
              allItems={allItems}
              onChange={async (next) => {
                await runSave(() =>
                  updateWbsItemKind(
                    getDb(),
                    item.projectId,
                    item,
                    next,
                    allItems,
                  ),
                );
              }}
            />
          </div>
          <InlineTitleEditor
            value={item.name}
            locked={backlogLocked || isShadow}
            onCommit={async (next) => {
              const trimmed = next.trim();
              if (!trimmed || trimmed === item.name) return;
              await runSave(() =>
                updateWbsItem(getDb(), item.projectId, item.itemId, {
                  name: trimmed,
                }),
              );
            }}
          />
          {backlogLocked && backlogIssueKey ? (
            <div className="mt-1">
              <BacklogLinkedBadge
                issueKey={backlogIssueKey}
                spaceUrl={project.backlog?.spaceUrl ?? null}
              />
            </div>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <AutosaveIndicator
            status={saveStatus}
            testId="wbs-detail-save-status"
          />
          <Button
            variant="ghost"
            // 44×44 minimum tap target on touch screens (Apple HIG /
            // Material) — the desktop chrome stays compact via lg:.
            className="!flex !h-11 !w-11 !items-center !justify-center !px-0 lg:!h-auto lg:!w-auto lg:!px-2"
            aria-label="削除"
            onClick={async () => {
              const msg = hasChildren
                ? `この行と配下の ${descendantIds.length} 件を削除しますか？`
                : 'この行を削除しますか？';
              if (!window.confirm(msg)) return;
              await deleteWbsItem(getDb(), pid, item.itemId, descendantIds);
              onClose();
            }}
          >
            <Trash2 className="h-5 w-5 text-red-500 lg:h-4 lg:w-4" />
          </Button>
          <Button
            variant="ghost"
            className="!flex !h-11 !w-11 !items-center !justify-center !px-0 lg:!h-auto lg:!w-auto lg:!px-2"
            onClick={onClose}
            aria-label="閉じる"
          >
            <X className="h-5 w-5 lg:h-4 lg:w-4" />
          </Button>
        </div>
      </header>
      <div
        // `min-h-0` lets the flex-1 body shrink below its content size so
        // the inner `overflow-auto` actually scrolls — without it the
        // default `min-height: auto` of a flex item grows the panel past
        // the viewport, hiding the last section (Backlog コメント) below
        // the fold. Same fix applies on lg+ (`lg:h-auto` aside stretched
        // by the shell's flex container) and below lg (`h-screen` aside).
        // `min-w-0` here so wide children (Backlog screenshots, long
        // URLs) can shrink instead of forcing horizontal scroll.
        // `overflow-x-hidden` belt-and-suspenders for the same reason.
        // Bottom padding respects the iOS home-indicator safe area so
        // the last edit field isn't hidden under the gesture bar.
        className="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden px-4 py-3 text-sm pb-[max(0.75rem,env(safe-area-inset-bottom))]"
        data-testid="wbs-detail-body"
      >
        <BacklogMilestoneMismatchBanner mismatch={item.backlogMilestoneMismatch} />
        {isShadow ? (
          <div
            data-testid="wbs-shadow-banner"
            className="mb-3 flex items-start gap-2 rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-[11px] text-slate-600"
          >
            <Eye className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            <p>
              これは別フェーズのタスクのシャドウ（参照）です。編集はできません。
              {shadowOriginal ? (
                <>
                  {' '}元タスク：
                  <button
                    type="button"
                    onClick={() => onSelectItem?.(shadowOriginal.itemId)}
                    className="font-medium text-indigo-600 underline hover:text-indigo-700"
                  >
                    {shadowOriginal.name || '(無題)'}
                  </button>
                  {shadowOriginalPhase ? `（${shadowOriginalPhase.name}）` : ''}
                </>
              ) : null}
            </p>
          </div>
        ) : null}
        {backlogLocked ? (
          <div
            data-testid="wbs-backlog-locked-banner"
            className="mb-3 flex items-start gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-[11px] text-indigo-800"
          >
            <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            <div className="min-w-0 flex-1">
              <p>Backlog 課題と連携中です。依存関係のみ編集可能です。</p>
              <p
                className="mt-1 text-indigo-700"
                data-testid="wbs-backlog-last-synced"
              >
                最終同期:{' '}
                {lockBannerSyncDate
                  ? formatRelativeFromNow(lockBannerSyncDate)
                  : '未取得'}
              </p>
            </div>
          </div>
        ) : null}
        {/* The tag row (領域 / ベンダー / 担当) stays at the top; below
            that, the order is now 期間 → 説明 so date entry is the
            first thing under the chips (most-edited fields nearest
            the top). */}
        <TagsRow
          item={item}
          assignees={assignees}
          areaOptions={areaOptions ?? []}
          areaColorIndices={areaColorIndices}
          vendorOptions={vendorOptions ?? []}
          vendorColorIndices={vendorColorIndices}
          runSave={runSave}
        />
        <BacklogSourceMetaSection item={item} />
        <BacklogOriginSection item={item} />
        <PeriodSection
          item={item}
          allItems={allItems}
          hasChildren={hasChildren}
          runSave={runSave}
        />
        <DescriptionField item={item} runSave={runSave} />
        {/* Comments sit directly under the description: read-flow
            (title → attrs → period → description → comments) stays a
            single block before the edit-flow sections (status, deps,
            Backlog 連携) below. */}
        {backlogLocked && backlogIssueKey ? (
          <BacklogCommentsSection
            projectId={pid}
            issueKey={backlogIssueKey}
            recentComments={primaryBacklogCacheEntry?.recentComments ?? []}
          />
        ) : null}
        {item.kind === 'phase' ? (
          <CompletionCriteriaField item={item} runSave={runSave} />
        ) : null}
        <StatusSection
          item={item}
          allItems={allItems}
          hasChildren={hasChildren}
          onSelectItem={onSelectItem}
          runSave={runSave}
        />
        <DependsOnSection
          item={item}
          allItems={allItems}
          assignees={assignees}
          onSelectItem={onSelectItem}
          runSave={runSave}
        />
        <SuccessorsSection
          item={item}
          allItems={allItems}
          assignees={assignees}
          onSelectItem={onSelectItem}
          runSave={runSave}
        />
        {item.kind === 'phase' ? (
          <PhaseBacklogMilestoneSection projectId={pid} phase={item} />
        ) : null}
        {item.kind === 'phase' ? (
          <PhaseBacklogCategorySection projectId={pid} phase={item} />
        ) : null}
        {item.kind === 'phase' ? (
          <PhaseBacklogParentIssueSection projectId={pid} phase={item} />
        ) : null}
        <BacklogSection
          item={item}
          allItems={allItems}
          backlogCache={backlogCache}
          spaceUrl={project.backlog?.spaceUrl ?? null}
          runSave={runSave}
        />
      </div>
    </aside>
  );
}

/**
 * Click-to-edit title in the panel header. Shows the row name with a
 * dashed underline on hover to hint at edit-ability. Click swaps the
 * label for an `<input>` that commits on blur / Enter and cancels on
 * Esc — the same UX as the WBS tree's name cell, so users learn one
 * gesture.
 */
function InlineTitleEditor({
  value,
  onCommit,
  locked = false,
}: {
  value: string;
  onCommit: (next: string) => void | Promise<void>;
  /** Backlog-mastered rows: title is read-only. */
  locked?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [expanded, setExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Reset the draft whenever the row changes — otherwise switching to a
  // sibling while the editor is open would keep stale text.
  useEffect(() => {
    setDraft(value);
    setEditing(false);
    setExpanded(false);
  }, [value]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  if (locked) {
    // Locked title is read-only but we still let the user tap to toggle
    // truncation — long Backlog issue titles like
    // 「【移行テスト④不具合】…」 routinely get truncated on phones, and
    // there is no other way to read the full string.
    return (
      <h2
        data-testid="wbs-detail-title"
        className={cn(
          'mt-0.5 cursor-pointer text-sm font-semibold text-slate-700 break-words [overflow-wrap:anywhere]',
          expanded ? '' : 'truncate',
        )}
        title={BACKLOG_LOCK_TOOLTIP}
        onClick={() => setExpanded((v) => !v)}
      >
        {value || '(無題)'}
      </h2>
    );
  }
  if (editing) {
    return (
      <input
        ref={inputRef}
        data-testid="wbs-detail-title-input"
        className="mt-0.5 w-full truncate rounded border border-sky-300 bg-white px-1 py-0.5 text-sm font-semibold outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-200"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          void onCommit(draft);
          setEditing(false);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            (e.target as HTMLInputElement).blur();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            setDraft(value);
            setEditing(false);
          }
        }}
      />
    );
  }
  return (
    <h2
      data-testid="wbs-detail-title"
      className="mt-0.5 cursor-text truncate text-sm font-semibold border-b border-dashed border-slate-300 hover:border-sky-400"
      onClick={() => setEditing(true)}
      title="クリックで編集"
    >
      {value || '(無題)'}
    </h2>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-4 rounded-md border border-slate-200 p-3">
      <h3 className="mb-2 text-xs font-semibold text-slate-600">{title}</h3>
      {children}
    </section>
  );
}

/**
 * Horizontal chip row: 領域 / ベンダー / 担当. Single-pick changes
 * autosave instantly (no debounce) — chip pickers commit per click,
 * so there's nothing to throttle. Local state mirrors the field for
 * immediate visual feedback while the Firestore write is in flight.
 */
function TagsRow({
  item,
  assignees,
  areaOptions,
  areaColorIndices,
  vendorOptions,
  vendorColorIndices,
  runSave,
}: {
  item: WbsItemDoc;
  assignees: readonly AssigneeDoc[];
  areaOptions: readonly string[];
  areaColorIndices?: AreaColorIndexMap;
  vendorOptions: readonly string[];
  vendorColorIndices?: AreaColorIndexMap;
  runSave: (op: () => Promise<void>) => Promise<void>;
}) {
  const locked = isWbsItemBacklogLinked(item) || isWbsItemShadow(item);
  const [systems, setSystems] = useState<string[]>(() =>
    readWbsItemSystems(item),
  );
  const [vendor, setVendor] = useState(item.vendor);
  // The picker stores `assigneeId`; "" means "未割当".
  const [assigneeId, setAssigneeId] = useState<string>(item.assigneeId ?? '');

  useEffect(() => {
    setSystems(readWbsItemSystems(item));
    setVendor(item.vendor);
    setAssigneeId(item.assigneeId ?? '');
    // `item` identity covers the dual-read'd systems/system change.
  }, [item]);

  const currentAssignee = useMemo(
    () =>
      assigneeId
        ? assignees.find((a) => a.assigneeId === assigneeId) ?? null
        : null,
    [assignees, assigneeId],
  );

  const persist = (
    patch: Partial<{
      systems: string[];
      vendor: string;
      assigneeId: string | null;
    }>,
  ) =>
    runSave(() => updateWbsItem(getDb(), item.projectId, item.itemId, patch));

  return (
    <div className="mb-3" data-testid="wbs-detail-basics">
      <div
        className="flex flex-wrap items-center gap-2 justify-start"
        data-testid="wbs-detail-tags-row"
      >
        <div className="inline-flex items-center gap-1" data-testid="wbs-detail-area">
          <span className="text-[11px] text-slate-500">領域</span>
          {locked ? (
            <span
              className="inline-flex flex-wrap items-center gap-0.5"
              title={BACKLOG_LOCK_TOOLTIP}
              data-testid="wbs-detail-area-locked"
            >
              {systems.length > 0 ? (
                systems.map((s) => (
                  <span
                    key={s}
                    className={cn(
                      'inline-flex items-center whitespace-nowrap rounded-md px-1.5 py-0.5 text-[11px] font-medium',
                      areaBadgeClasses(s, areaColorIndices),
                    )}
                  >
                    {s}
                  </span>
                ))
              ) : (
                <span className="text-xs text-slate-400">—</span>
              )}
              <BacklogLockMark />
            </span>
          ) : (
            <AreaMultiPicker
              value={systems}
              options={areaOptions}
              colorIndices={areaColorIndices}
              onChange={async (next) => {
                setSystems(next);
                await persist({ systems: next });
              }}
            />
          )}
        </div>
        <ChipPicker
          label="ベンダー"
          value={vendor}
          options={vendorOptions}
          getBadgeClasses={(v) => areaBadgeClasses(v, vendorColorIndices)}
          onChange={async (next) => {
            setVendor(next);
            await persist({ vendor: next });
          }}
        />
        {locked ? (
          <span
            className="inline-flex items-center gap-1"
            title={BACKLOG_LOCK_TOOLTIP}
            data-testid="wbs-detail-assignee-locked"
          >
            <span className="text-[11px] text-slate-500">担当</span>
            <span className="text-xs text-slate-700">
              {currentAssignee?.name ?? '未割当'}
            </span>
            <BacklogLockMark />
          </span>
        ) : (
          <AssigneeChipPicker
            assignees={assignees}
            assigneeId={assigneeId}
            currentAssignee={currentAssignee}
            onChange={async (nextId) => {
              setAssigneeId(nextId);
              await persist({ assigneeId: nextId === '' ? null : nextId });
            }}
          />
        )}
        {assignees.length === 0 ? (
          <p className="basis-full text-[11px] text-amber-600">
            担当者リストが空です。設定画面 →「担当者リスト」から追加してください。
          </p>
        ) : null}
      </div>
    </div>
  );
}

/**
 * 説明 textarea with debounced autosave. Local `draft` state keeps the
 * caret responsive while the Firestore write is throttled to one call
 * per ~{@link AUTOSAVE_DEBOUNCE_MS}ms typing burst. The previous "保存"
 * button is gone — there's no escape hatch needed because every keystroke
 * eventually persists.
 */
function DescriptionField({
  item,
  runSave,
}: {
  item: WbsItemDoc;
  runSave: (op: () => Promise<void>) => Promise<void>;
}) {
  const locked = isWbsItemBacklogLinked(item) || isWbsItemShadow(item);
  const [draft, setDraft] = useState(item.description);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track the in-flight value so an item-switch / unmount doesn't drop
  // the user's last typing burst — we flush immediately when the
  // dependency captures a new itemId.
  const pendingRef = useRef<{
    projectId: string;
    itemId: string;
    value: string;
  } | null>(null);

  // When the row switches (or the canonical description changes from
  // the server), reset the local draft. Flush any pending save FIRST so
  // we don't drop typing the user did right before clicking another row.
  useEffect(() => {
    if (timerRef.current && pendingRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
      const { projectId, itemId, value } = pendingRef.current;
      pendingRef.current = null;
      void runSave(() =>
        updateWbsItem(getDb(), projectId, itemId, { description: value }),
      );
    }
    setDraft(item.description);
  }, [item.itemId, item.description, runSave]);

  // On unmount (panel close), flush any pending save so the user's
  // last typing burst hits Firestore. The timer would otherwise be GC'd.
  useEffect(() => {
    return () => {
      if (timerRef.current && pendingRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
        const { projectId, itemId, value } = pendingRef.current;
        pendingRef.current = null;
        void runSave(() =>
          updateWbsItem(getDb(), projectId, itemId, { description: value }),
        );
      }
    };
  }, [runSave]);

  const onChange = (next: string) => {
    setDraft(next);
    if (timerRef.current) clearTimeout(timerRef.current);
    pendingRef.current = {
      projectId: item.projectId,
      itemId: item.itemId,
      value: next,
    };
    timerRef.current = setTimeout(() => {
      const pending = pendingRef.current;
      pendingRef.current = null;
      timerRef.current = null;
      if (!pending) return;
      void runSave(() =>
        updateWbsItem(getDb(), pending.projectId, pending.itemId, {
          description: pending.value,
        }),
      );
    }, AUTOSAVE_DEBOUNCE_MS);
  };

  // When the row is Backlog-mastered we render the description in
  // read-only mode and inflate the Backlog image syntax (`![image][...]`)
  // into actual `<img>` tags via `BacklogImageContent`. Manual rows
  // keep the live textarea since the body is plain text.
  const backlogIssueKey = primaryBacklogIssueKey(item);
  const showAsBacklogContent = locked && backlogIssueKey != null;

  return (
    <div className="mb-4" data-testid="wbs-detail-description">
      <div className="flex items-center">
        <Label>説明</Label>
        {locked ? <BacklogLockMark /> : null}
      </div>
      {showAsBacklogContent ? (
        <div
          data-testid="wbs-detail-description-readonly"
          title={BACKLOG_LOCK_TOOLTIP}
          className="input min-h-[6rem] w-full min-w-0 overflow-x-hidden whitespace-pre-wrap break-words [overflow-wrap:anywhere] bg-slate-50 text-slate-700"
        >
          {draft ? (
            <BacklogImageContent
              projectId={item.projectId}
              issueKey={backlogIssueKey}
              text={draft}
            />
          ) : (
            <span className="text-slate-400">(説明なし)</span>
          )}
        </div>
      ) : locked ? (
        <div
          data-testid="wbs-detail-description-readonly"
          title={BACKLOG_LOCK_TOOLTIP}
          className="input min-h-[6rem] w-full min-w-0 overflow-x-hidden whitespace-pre-wrap break-words [overflow-wrap:anywhere] bg-slate-50 text-slate-700"
        >
          {draft || (
            <span className="text-slate-400">(説明なし)</span>
          )}
        </div>
      ) : (
        <AutoGrowTextarea
          value={draft}
          minRows={5}
          onChange={(next) => onChange(next)}
        />
      )}
    </div>
  );
}

/**
 * `<Textarea>` that grows with its content. Uses CSS `field-sizing:
 * content` where supported (Chrome 123+ / Safari 17.4+) and falls back
 * to a JS resize that sets `height = scrollHeight` on every input. The
 * minimum row count is enforced via `min-height` (line-height × minRows)
 * so an empty box still feels like an editable area.
 */
function AutoGrowTextarea({
  value,
  minRows,
  onChange,
}: {
  value: string;
  minRows: number;
  onChange: (next: string) => void;
}) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const resize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);
  // Resize when external value changes (item switch / autosave reset).
  useEffect(() => {
    resize();
  }, [value, resize]);
  return (
    <Textarea
      ref={ref}
      value={value}
      onChange={(e) => {
        onChange(e.target.value);
        resize();
      }}
      rows={minRows}
      style={{
        // Chrome / Safari natively size to content; the JS fallback
        // above covers Firefox and older browsers. `overflow:hidden`
        // hides the scrollbar that would otherwise flash while
        // scrollHeight is being measured.
        fieldSizing: 'content',
        overflow: 'hidden',
      } as React.CSSProperties}
    />
  );
}

/**
 * フェーズ専用の「完了基準」textarea. Phase に紐づくマイグレーション
 * タスクが満たすべき共通の合格基準を 1 つだけ持つ — フェーズ単位の
 * フィールドなので、タスク側は read-only でこの値を参照する。
 * 説明欄と同じ debounce 自動保存パターンで、{@link
 * WBS_COMPLETION_CRITERIA_MAX_LENGTH} 文字を超えた瞬間にローカル状態
 * は保ったまま保存を止め、警告メッセージとカウンタを赤くする。
 */
function CompletionCriteriaField({
  item,
  runSave,
}: {
  item: WbsItemDoc;
  runSave: (op: () => Promise<void>) => Promise<void>;
}) {
  const initial = item.completionCriteria ?? '';
  const [draft, setDraft] = useState(initial);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<{
    projectId: string;
    itemId: string;
    value: string;
  } | null>(null);

  useEffect(() => {
    if (timerRef.current && pendingRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
      const { projectId, itemId, value } = pendingRef.current;
      pendingRef.current = null;
      if (value.length <= WBS_COMPLETION_CRITERIA_MAX_LENGTH) {
        void runSave(() =>
          updateWbsItem(getDb(), projectId, itemId, {
            completionCriteria: value === '' ? null : value,
          }),
        );
      }
    }
    setDraft(item.completionCriteria ?? '');
  }, [item.itemId, item.completionCriteria, runSave]);

  useEffect(() => {
    return () => {
      if (timerRef.current && pendingRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
        const { projectId, itemId, value } = pendingRef.current;
        pendingRef.current = null;
        if (value.length <= WBS_COMPLETION_CRITERIA_MAX_LENGTH) {
          void runSave(() =>
            updateWbsItem(getDb(), projectId, itemId, {
              completionCriteria: value === '' ? null : value,
            }),
          );
        }
      }
    };
  }, [runSave]);

  const onChange = (next: string) => {
    setDraft(next);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (next.length > WBS_COMPLETION_CRITERIA_MAX_LENGTH) {
      pendingRef.current = null;
      timerRef.current = null;
      return;
    }
    pendingRef.current = {
      projectId: item.projectId,
      itemId: item.itemId,
      value: next,
    };
    timerRef.current = setTimeout(() => {
      const pending = pendingRef.current;
      pendingRef.current = null;
      timerRef.current = null;
      if (!pending) return;
      void runSave(() =>
        updateWbsItem(getDb(), pending.projectId, pending.itemId, {
          completionCriteria: pending.value === '' ? null : pending.value,
        }),
      );
    }, AUTOSAVE_DEBOUNCE_MS);
  };

  const length = draft.length;
  const overLimit = length > WBS_COMPLETION_CRITERIA_MAX_LENGTH;
  const nearLimit =
    !overLimit && length >= WBS_COMPLETION_CRITERIA_MAX_LENGTH - 200;

  return (
    <div className="mb-4" data-testid="wbs-detail-completion-criteria">
      <div className="flex items-baseline justify-between">
        <Label>完了基準</Label>
        <span
          data-testid="wbs-detail-completion-criteria-counter"
          className={cn(
            'text-[10px]',
            overLimit
              ? 'text-red-600'
              : nearLimit
                ? 'text-amber-600'
                : 'text-slate-400',
          )}
        >
          {length} / {WBS_COMPLETION_CRITERIA_MAX_LENGTH}
        </span>
      </div>
      <Textarea
        value={draft}
        onChange={(e) => onChange(e.target.value)}
        rows={5}
        placeholder="このフェーズに含まれるタスクが満たすべき完了条件をまとめて記入してください（改行可）"
        aria-invalid={overLimit ? true : undefined}
        className={overLimit ? 'border-red-400 focus:border-red-500' : undefined}
      />
      {overLimit ? (
        <p className="mt-1 text-[11px] text-red-600">
          完了基準は {WBS_COMPLETION_CRITERIA_MAX_LENGTH} 文字以内で入力してください
        </p>
      ) : null}
    </div>
  );
}

/**
 * Compact label + chip-with-popover. Click the chip to open a small
 * dropdown with the catalog options (plus a "未設定" entry that clears
 * the value). Chip rendering reuses {@link areaBadgeClasses} so the
 * detail-panel chip matches the WBS tree cell pixel-for-pixel.
 */
function ChipPicker({
  label,
  value,
  options,
  getBadgeClasses,
  onChange,
}: {
  label: string;
  value: string;
  options: readonly string[];
  getBadgeClasses: (v: string) => string;
  onChange: (next: string) => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current) return;
      if (ref.current.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const t = window.setTimeout(() => {
      document.addEventListener('mousedown', onDown);
      document.addEventListener('keydown', onKey);
    }, 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative inline-flex items-center gap-1">
      <span className="text-[11px] text-slate-500">{label}:</span>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-medium',
          value
            ? getBadgeClasses(value)
            : 'border border-dashed border-slate-300 text-slate-400',
        )}
        data-testid={`wbs-detail-chip-${label}`}
        aria-label={`${label}を変更`}
      >
        {value || '未設定'}
      </button>
      {open ? (
        <div className="absolute left-0 top-full z-30 mt-1 max-h-72 w-48 overflow-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg">
          <button
            type="button"
            className={cn(
              'block w-full px-2 py-1 text-left text-xs hover:bg-slate-50',
              value === '' ? 'bg-slate-100' : '',
            )}
            onClick={() => {
              setOpen(false);
              void onChange('');
            }}
          >
            <span className="text-slate-500">(未設定)</span>
          </button>
          {options.map((opt) => (
            <button
              key={opt}
              type="button"
              className={cn(
                'block w-full px-2 py-1 text-left text-xs hover:bg-slate-50',
                value === opt ? 'bg-slate-100' : '',
              )}
              onClick={() => {
                setOpen(false);
                void onChange(opt);
              }}
            >
              <span
                className={cn(
                  'inline-block rounded px-1.5 py-0.5 text-[11px]',
                  getBadgeClasses(opt),
                )}
              >
                {opt}
              </span>
            </button>
          ))}
          {options.length === 0 ? (
            <div className="px-2 py-2 text-center text-[11px] text-slate-400">
              選択肢がありません
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * 期間 (or 日付 for milestones). Flat layout — no border / padding box —
 * with autosave on every commit:
 *   - DateInput already commits on blur / picker-pick, so we forward
 *     each change straight to Firestore (no extra debounce).
 *   - The duration-days input was removed; the schema field
 *     ({@link WbsItemDoc.durationDays}) is preserved because
 *     `aggregateProgress` still uses it as a child weight.
 *   - The "子行から自動集計" banner is gone; the same explanation now
 *     surfaces as a `title` tooltip on the disabled inputs.
 */
function PeriodSection({
  item,
  allItems,
  hasChildren,
  runSave,
}: {
  item: WbsItemDoc;
  allItems: readonly WbsItemDoc[];
  hasChildren: boolean;
  runSave: (op: () => Promise<void>) => Promise<void>;
}) {
  const [plannedStart, setPlannedStart] = useState(
    toDateInput(tsToDate(item.plannedStart)),
  );
  const [plannedEnd, setPlannedEnd] = useState(
    toDateInput(tsToDate(item.plannedEnd)),
  );
  const [actualStart, setActualStart] = useState(
    toDateInput(tsToDate(item.actualStart)),
  );
  const [actualEnd, setActualEnd] = useState(
    toDateInput(tsToDate(item.actualEnd)),
  );

  useEffect(() => {
    setPlannedStart(toDateInput(tsToDate(item.plannedStart)));
    setPlannedEnd(toDateInput(tsToDate(item.plannedEnd)));
    setActualStart(toDateInput(tsToDate(item.actualStart)));
    setActualEnd(toDateInput(tsToDate(item.actualEnd)));
  }, [
    item.itemId,
    item.plannedStart,
    item.plannedEnd,
    item.actualStart,
    item.actualEnd,
  ]);

  // Planned dates are locked either by child rollup (existing) or because
  // the row is Backlog-mastered. Actuals stay editable in both cases.
  const backlogLocked = isWbsItemBacklogLinked(item);
  const dateDisabled = hasChildren || backlogLocked;

  const startViolation = useMemo(
    () =>
      dateDisabled
        ? null
        : validatePlannedStart(
            parseDateInput(plannedStart),
            item,
            allItems,
          ),
    [plannedStart, item, allItems, dateDisabled],
  );
  const endViolation = useMemo(
    () =>
      dateDisabled
        ? null
        : validatePlannedEnd(parseDateInput(plannedEnd), item, allItems),
    [plannedEnd, item, allItems, dateDisabled],
  );

  const isMilestone = item.kind === 'milestone';

  const dateTooltip = backlogLocked ? BACKLOG_LOCK_TOOLTIP : ROLLUP_TOOLTIP;

  const persistDate = (
    patch: Partial<{
      plannedStart: Date | null;
      plannedEnd: Date | null;
      actualStart: Date | null;
      actualEnd: Date | null;
    }>,
  ) => runSave(() => updateWbsItem(getDb(), item.projectId, item.itemId, patch));

  return (
    <section
      className="mb-4"
      data-testid="wbs-detail-period"
      data-flat="true"
    >
      <h3 className="mb-1 text-xs font-semibold text-slate-500">
        {isMilestone ? '日付' : '期間'}
      </h3>
      <div className="grid grid-cols-2 gap-2">
        <div
          className={isMilestone ? 'col-span-2' : 'col-span-1'}
          // Hover the disabled wrapper to learn why it's locked. We
          // attach the title to the surrounding div so the tooltip
          // surfaces even when the user mouses over the calendar
          // button (which has its own title for the popover).
          title={dateDisabled ? dateTooltip : undefined}
          data-testid="wbs-detail-planned-start"
        >
          <Label>{isMilestone ? '日付' : '予定開始'}</Label>
          <DateInput
            disabled={dateDisabled}
            value={plannedStart}
            onChange={(next) => {
              if (dateDisabled) return;
              setPlannedStart(next);
              const patch: {
                plannedStart: Date | null;
                plannedEnd?: Date | null;
              } = { plannedStart: parseDateInput(next) };
              if (isMilestone) {
                // Milestones store the same date in both planned* fields
                // so the Gantt's range math (`tsToDate(end)`) keeps
                // working without per-kind branching.
                setPlannedEnd(next);
                patch.plannedEnd = parseDateInput(next);
              }
              void persistDate(patch);
            }}
            invalid={Boolean(startViolation)}
          />
          {startViolation ? (
            <p className="mt-1 text-[11px] text-red-600">
              {startViolation.message}
            </p>
          ) : null}
        </div>
        {isMilestone ? null : (
          <div
            className="col-span-1"
            title={dateDisabled ? dateTooltip : undefined}
            data-testid="wbs-detail-planned-end"
          >
            <Label>予定終了</Label>
            <DateInput
              disabled={dateDisabled}
              value={plannedEnd}
              onChange={(next) => {
                if (dateDisabled) return;
                setPlannedEnd(next);
                void persistDate({ plannedEnd: parseDateInput(next) });
              }}
              invalid={Boolean(endViolation)}
            />
            {endViolation ? (
              <p className="mt-1 text-[11px] text-red-600">
                {endViolation.message}
              </p>
            ) : null}
          </div>
        )}
        <div>
          <Label>実績開始</Label>
          <DateInput
            value={actualStart}
            onChange={(next) => {
              setActualStart(next);
              void persistDate({ actualStart: parseDateInput(next) });
            }}
          />
        </div>
        <div>
          <Label>実績終了</Label>
          <DateInput
            value={actualEnd}
            onChange={(next) => {
              setActualEnd(next);
              void persistDate({ actualEnd: parseDateInput(next) });
            }}
          />
        </div>
      </div>
    </section>
  );
}

function StatusSection({
  item,
  allItems,
  hasChildren,
  onSelectItem,
  runSave,
}: {
  item: WbsItemDoc;
  allItems: readonly WbsItemDoc[];
  hasChildren: boolean;
  onSelectItem?: (itemId: string) => void;
  runSave: (op: () => Promise<void>) => Promise<void>;
}) {
  const progressLocked =
    (hasChildren && item.progressMode === 'auto') || isWbsItemShadow(item);
  // Phases and any row with children are status-rolled-up by
  // `aggregateWbsParent`; direct edits would be overwritten on the
  // next child write. Lock the select with an explanatory tooltip
  // (matches the WBS list cell behaviour). Backlog-mastered rows are
  // also locked (Backlog owns the status). Progress stays editable.
  const backlogLocked = isWbsItemBacklogLinked(item);
  const statusLocked = item.kind === 'phase' || hasChildren || backlogLocked;
  // Mirror the dependency-chip scroll-into-view UX: bubble up the id
  // and best-effort scroll the corresponding tree row into view once
  // React commits the new selection. Children that are currently
  // collapsed silently no-op (no `data-wbs-item-id` element).
  const handleChildSelect = (id: string) => {
    onSelectItem?.(id);
    if (typeof window === 'undefined') return;
    window.requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>(
        `[data-wbs-item-id="${id}"]`,
      );
      el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  };
  const backlogAutoUpdatedAt = tsToDate(item.backlogAutoUpdatedAt ?? null);
  return (
    <Section title="ステータス / 進捗">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-y-0.5">
            <Label>ステータス</Label>
            <BacklogAutoBadge updatedAt={backlogAutoUpdatedAt} />
            {backlogLocked ? <BacklogLockMark /> : null}
          </div>
          <select
            className="input"
            value={item.status}
            disabled={statusLocked}
            title={
              backlogLocked
                ? BACKLOG_LOCK_TOOLTIP
                : statusLocked
                  ? '子行から自動集計 — 直接変更できません'
                  : undefined
            }
            onChange={(e) => {
              void runSave(() =>
                updateWbsItem(getDb(), item.projectId, item.itemId, {
                  status: e.target.value as WbsItemStatus,
                }),
              );
            }}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
        <div className="min-w-0">
          <Label>進捗 (%)</Label>
          <Input
            type="number"
            min={0}
            max={100}
            disabled={progressLocked}
            value={item.progress}
            onChange={(e) => {
              const next = Math.max(
                0,
                Math.min(100, Number(e.target.value) || 0),
              );
              void runSave(() =>
                updateWbsItem(getDb(), item.projectId, item.itemId, {
                  progress: next,
                }),
              );
            }}
          />
        </div>
      </div>
      {hasChildren ? (
        <div className="mt-2">
          <label className="inline-flex items-center gap-1 text-xs text-slate-700">
            <input
              type="checkbox"
              checked={item.progressMode === 'auto'}
              onChange={(e) => {
                const mode: WbsProgressMode = e.target.checked
                  ? 'auto'
                  : 'manual';
                void runSave(() =>
                  updateWbsItem(getDb(), item.projectId, item.itemId, {
                    progressMode: mode,
                  }),
                );
              }}
            />
            自動集計 (progressMode=auto)
          </label>
          <p className="mt-1 text-[11px] text-slate-500">
            チェック ON で Cloud Function が子行の加重平均を書き戻します
          </p>
        </div>
      ) : null}
      {hasChildren ? (
        <RollupChildren
          parentId={item.itemId}
          allItems={allItems}
          onSelectItem={onSelectItem ? handleChildSelect : undefined}
        />
      ) : null}
    </Section>
  );
}

function DependsOnSection({
  item,
  allItems,
  assignees,
  onSelectItem,
  runSave,
}: {
  item: WbsItemDoc;
  allItems: readonly WbsItemDoc[];
  assignees: readonly AssigneeDoc[];
  onSelectItem?: (itemId: string) => void;
  runSave: (op: () => Promise<void>) => Promise<void>;
}) {
  // Wraps the page's `setSelectedId` with a best-effort scroll into view
  // once React has had a frame to render the new selection. Falls back to
  // doing nothing when the targeted row is currently collapsed (its
  // `data-wbs-item-id` element won't exist), which is a tolerable miss.
  const handleSelect = (id: string) => {
    onSelectItem?.(id);
    if (typeof window === 'undefined') return;
    window.requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>(
        `[data-wbs-item-id="${id}"]`,
      );
      el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  };
  return (
    <Section title="先行タスク (dependsOn)">
      <DependencyChips
        item={item}
        allItems={allItems}
        assignees={assignees}
        onChange={async (next) => {
          await runSave(() =>
            updateWbsItem(getDb(), item.projectId, item.itemId, {
              dependsOn: next,
            }),
          );
        }}
        onSelectItem={onSelectItem ? handleSelect : undefined}
      />
    </Section>
  );
}

/**
 * Editable counterpart to {@link DependsOnSection}: lists items whose
 * `dependsOn` contains this row. Add / remove writes to the *successor*
 * document's `dependsOn`, not to the current item — the bidirectional
 * graph is single-sourced on the successor side, so flipping the
 * successor side stays consistent with the predecessor side.
 */
function SuccessorsSection({
  item,
  allItems,
  assignees,
  onSelectItem,
  runSave,
}: {
  item: WbsItemDoc;
  allItems: readonly WbsItemDoc[];
  assignees: readonly AssigneeDoc[];
  onSelectItem?: (itemId: string) => void;
  runSave: (op: () => Promise<void>) => Promise<void>;
}) {
  // Same scroll-into-view dance as `DependsOnSection.handleSelect` —
  // wait one frame for React to render the new selection, then nudge
  // the row into view. Tolerates the row being collapsed (the query
  // returns null and we silently skip).
  const handleSelect = (id: string) => {
    onSelectItem?.(id);
    if (typeof window === 'undefined') return;
    window.requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>(
        `[data-wbs-item-id="${id}"]`,
      );
      el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  };
  // Resolve the successor doc each time so the closure picks up the
  // latest dependsOn array even if Firestore snapshot updates rerender.
  const itemsById = useMemo(
    () => new Map(allItems.map((i) => [i.itemId, i] as const)),
    [allItems],
  );
  return (
    <Section title="後続タスク (このタスクに依存する項目)">
      <SuccessorChips
        item={item}
        allItems={allItems}
        assignees={assignees}
        onAddSuccessor={async (otherId) => {
          const other = itemsById.get(otherId);
          if (!other) return;
          if (other.dependsOn.includes(item.itemId)) return;
          await runSave(() =>
            updateWbsItem(getDb(), item.projectId, otherId, {
              dependsOn: [...other.dependsOn, item.itemId],
            }),
          );
        }}
        onRemoveSuccessor={async (otherId) => {
          const other = itemsById.get(otherId);
          if (!other) return;
          await runSave(() =>
            updateWbsItem(getDb(), item.projectId, otherId, {
              dependsOn: other.dependsOn.filter((d) => d !== item.itemId),
            }),
          );
        }}
        onSelectItem={onSelectItem ? handleSelect : undefined}
      />
    </Section>
  );
}

/**
 * Phase-only section: a list of Backlog-milestone links on this phase.
 *
 * Two states:
 *   - No links → CTA opens {@link PhaseBacklogLinkDialog}.
 *   - One or more links → each entry rendered as a row with
 *     "今すぐ同期" + "連携を解除" controls; a "マイルストンを追加" button
 *     opens the same dialog to append another entry (incl. milestones
 *     from a different Backlog project under the same workspace).
 *
 * The link list is read straight off the phase doc (which the parent
 * already streams). Callables for link/unlink/sync write back to the
 * doc, so once they resolve the section re-renders from the live
 * snapshot — no local mirror state.
 */
function PhaseBacklogMilestoneSection({
  projectId,
  phase,
}: {
  projectId: string;
  phase: WbsItemDoc;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{
    kind: 'info' | 'error';
    message: string;
  } | null>(null);
  const links: PhaseBacklogMilestoneLink[] = Array.isArray(
    phase.backlogMilestoneLinks,
  )
    ? phase.backlogMilestoneLinks
    : [];
  const existingKeys = useMemo(
    () =>
      new Set(
        links.map((l) => `${l.backlogProjectKey}:${l.milestoneId}`),
      ),
    [links],
  );

  const handleSyncOne = async (link: PhaseBacklogMilestoneLink) => {
    const opKey = `sync:${link.backlogProjectKey}:${link.milestoneId}`;
    setBusyKey(opKey);
    setFeedback(null);
    try {
      const res = await syncBacklogMilestoneNow({
        projectId,
        phaseId: phase.itemId,
        backlogProjectKey: link.backlogProjectKey,
        milestoneId: link.milestoneId,
      });
      if (!res.ok) {
        setFeedback({ kind: 'error', message: res.message });
        return;
      }
      setFeedback({
        kind: 'info',
        message: `${link.milestoneName} を同期 — 新規 ${res.added.length} / 解除 ${res.removed.length} / 既存 ${res.unchanged.length}`,
      });
    } catch (err) {
      setFeedback({
        kind: 'error',
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setBusyKey(null);
    }
  };

  const handleUnlinkOne = async (link: PhaseBacklogMilestoneLink) => {
    const removeImported = window.confirm(
      `「${link.milestoneName}」から取り込んだ課題（このマイルストン由来の WBS タスク）も一緒に削除しますか？\n\n[OK] 削除する  [キャンセル] 行は残してリンクのみ解除`,
    );
    const opKey = `unlink:${link.backlogProjectKey}:${link.milestoneId}`;
    setBusyKey(opKey);
    setFeedback(null);
    try {
      const res = await unlinkPhaseFromBacklogMilestone({
        projectId,
        phaseId: phase.itemId,
        backlogProjectKey: link.backlogProjectKey,
        milestoneId: link.milestoneId,
        removeImportedItems: removeImported,
      });
      if (!res.ok) {
        setFeedback({ kind: 'error', message: res.message });
        return;
      }
      const detail = removeImported
        ? `${res.removedItemCount} 件を削除`
        : `${res.clearedItemCount} 件のリンクを解除`;
      setFeedback({
        kind: 'info',
        message: `${link.milestoneName} の連携を解除しました（${detail}）`,
      });
    } catch (err) {
      setFeedback({
        kind: 'error',
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <Section title="Backlog マイルストン連携">
      {links.length > 0 ? (
        <div className="space-y-3" data-testid="phase-milestone-linked">
          <ul className="space-y-2">
            {links.map((link) => {
              const lastSyncDate = tsToDate(link.lastSyncedAt ?? null);
              const entryKey = `${link.backlogProjectKey}:${link.milestoneId}`;
              const isSyncing = busyKey === `sync:${entryKey}`;
              const isUnlinking = busyKey === `unlink:${entryKey}`;
              const otherBusy = busyKey != null && !isSyncing && !isUnlinking;
              return (
                <li
                  key={entryKey}
                  className="rounded border border-slate-200 bg-white px-2 py-1.5"
                  data-testid="phase-milestone-link-row"
                >
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="inline-flex items-center gap-1 rounded bg-sky-50 px-1.5 py-0.5 font-medium text-sky-700">
                      <LinkIcon className="h-3 w-3" />
                      {formatBacklogProjectLabel(
                        link.backlogProjectKey,
                        link.backlogProjectName,
                      )}
                      <span className="text-sky-400">/</span>
                      {link.milestoneName}
                    </span>
                    <span className="text-[11px] text-slate-500">
                      {lastSyncDate
                        ? `最終同期 ${formatRelativeFromNow(lastSyncDate)}`
                        : '未同期'}
                    </span>
                    {link.lastSyncError ? (
                      <span
                        className="rounded bg-red-50 px-1.5 py-0.5 text-[11px] text-red-700"
                        title={link.lastSyncError}
                      >
                        最終同期エラー
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <Button
                      variant="secondary"
                      onClick={() => handleSyncOne(link)}
                      disabled={isSyncing || isUnlinking || otherBusy}
                      className="!py-1 !text-xs"
                      data-testid="phase-milestone-sync-now"
                    >
                      {isSyncing ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Spinner className="h-3 w-3" /> 同期中…
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5">
                          <RefreshCw className="h-3 w-3" /> 今すぐ同期
                        </span>
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => handleUnlinkOne(link)}
                      disabled={isSyncing || isUnlinking || otherBusy}
                      className="!py-1 !text-xs text-red-600 hover:text-red-700"
                      data-testid="phase-milestone-unlink"
                    >
                      {isUnlinking ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Spinner className="h-3 w-3" /> 解除中…
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5">
                          <Unlink className="h-3 w-3" /> 連携を解除
                        </span>
                      )}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="flex items-center">
            <Button
              variant="secondary"
              onClick={() => setDialogOpen(true)}
              disabled={busyKey != null}
              className="!py-1 !text-xs"
              data-testid="phase-milestone-add"
            >
              <span className="inline-flex items-center gap-1.5">
                <LinkIcon className="h-3 w-3" /> マイルストンを追加
              </span>
            </Button>
          </div>
          {feedback ? (
            <p
              className={
                feedback.kind === 'error'
                  ? 'text-[11px] text-red-600'
                  : 'text-[11px] text-sky-700'
              }
              data-testid="phase-milestone-feedback"
            >
              {feedback.message}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="space-y-1" data-testid="phase-milestone-unlinked">
          <p className="text-[11px] text-slate-500">
            Backlog のマイルストン配下の課題を、このフェーズ直下の WBS タスクとして自動取り込みできます。複数のマイルストン（異プロジェクトを含む）を紐付けることもできます。
          </p>
          <Button
            variant="secondary"
            onClick={() => setDialogOpen(true)}
            className="!py-1 !text-xs"
            data-testid="phase-milestone-link-cta"
          >
            <span className="inline-flex items-center gap-1.5">
              <LinkIcon className="h-3 w-3" /> マイルストンと連携
            </span>
          </Button>
          {feedback ? (
            <p
              className={
                feedback.kind === 'error'
                  ? 'text-[11px] text-red-600'
                  : 'text-[11px] text-sky-700'
              }
              data-testid="phase-milestone-feedback"
            >
              {feedback.message}
            </p>
          ) : null}
        </div>
      )}
      <PhaseBacklogLinkDialog
        open={dialogOpen}
        projectId={projectId}
        phaseId={phase.itemId}
        phaseName={phase.name || '(無題)'}
        existingLinkKeys={existingKeys}
        onClose={() => setDialogOpen(false)}
        onLinked={(res) => {
          if (res.ok) {
            setFeedback({
              kind: 'info',
              message: `${res.milestone.name} と連携しました — 新規 ${res.added.length} 件 / 既存 ${res.unchanged.length} 件${res.truncated ? '（上限到達のため一部省略）' : ''}`,
            });
          }
        }}
      />
    </Section>
  );
}

/**
 * Phase-only section: a list of Backlog **category** links on this phase.
 * The category analogue of {@link PhaseBacklogMilestoneSection}.
 *
 * Two states:
 *   - No links → CTA opens {@link PhaseBacklogCategoryLinkDialog}.
 *   - One or more links → each entry rendered as a row with "今すぐ同期"
 *     + "連携を解除" controls; a "カテゴリを追加" button appends another.
 */
function PhaseBacklogCategorySection({
  projectId,
  phase,
}: {
  projectId: string;
  phase: WbsItemDoc;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{
    kind: 'info' | 'error';
    message: string;
  } | null>(null);
  const links: PhaseBacklogCategoryLink[] = Array.isArray(
    phase.backlogCategoryLinks,
  )
    ? phase.backlogCategoryLinks
    : [];
  const existingKeys = useMemo(
    () =>
      new Set(links.map((l) => `${l.backlogProjectKey}:${l.categoryId}`)),
    [links],
  );

  const handleSyncOne = async (link: PhaseBacklogCategoryLink) => {
    const opKey = `sync:${link.backlogProjectKey}:${link.categoryId}`;
    setBusyKey(opKey);
    setFeedback(null);
    try {
      const res = await syncBacklogCategoryNow({
        projectId,
        phaseId: phase.itemId,
        backlogProjectKey: link.backlogProjectKey,
        categoryId: link.categoryId,
      });
      if (!res.ok) {
        setFeedback({ kind: 'error', message: res.message });
        return;
      }
      setFeedback({
        kind: 'info',
        message: `${link.categoryName} を同期 — 新規 ${res.added.length} / 解除 ${res.removed.length} / 既存 ${res.unchanged.length}`,
      });
    } catch (err) {
      setFeedback({
        kind: 'error',
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setBusyKey(null);
    }
  };

  const handleUnlinkOne = async (link: PhaseBacklogCategoryLink) => {
    const removeImported = window.confirm(
      `「${link.categoryName}」から取り込んだ課題（このカテゴリ由来の WBS タスク）も一緒に削除しますか？\n\n[OK] 削除する  [キャンセル] 行は残してリンクのみ解除`,
    );
    const opKey = `unlink:${link.backlogProjectKey}:${link.categoryId}`;
    setBusyKey(opKey);
    setFeedback(null);
    try {
      const res = await unlinkPhaseFromBacklogCategory({
        projectId,
        phaseId: phase.itemId,
        backlogProjectKey: link.backlogProjectKey,
        categoryId: link.categoryId,
        removeImportedItems: removeImported,
      });
      if (!res.ok) {
        setFeedback({ kind: 'error', message: res.message });
        return;
      }
      const detail = removeImported
        ? `${res.removedItemCount} 件を削除`
        : `${res.clearedItemCount} 件のリンクを解除`;
      setFeedback({
        kind: 'info',
        message: `${link.categoryName} の連携を解除しました（${detail}）`,
      });
    } catch (err) {
      setFeedback({
        kind: 'error',
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <Section title="Backlog カテゴリ連携">
      {links.length > 0 ? (
        <div className="space-y-3" data-testid="phase-category-linked">
          <ul className="space-y-2">
            {links.map((link) => {
              const lastSyncDate = tsToDate(link.lastSyncedAt ?? null);
              const entryKey = `${link.backlogProjectKey}:${link.categoryId}`;
              const isSyncing = busyKey === `sync:${entryKey}`;
              const isUnlinking = busyKey === `unlink:${entryKey}`;
              const otherBusy = busyKey != null && !isSyncing && !isUnlinking;
              return (
                <li
                  key={entryKey}
                  className="rounded border border-slate-200 bg-white px-2 py-1.5"
                  data-testid="phase-category-link-row"
                >
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 font-medium text-emerald-700">
                      <LinkIcon className="h-3 w-3" />
                      {formatBacklogProjectLabel(
                        link.backlogProjectKey,
                        link.backlogProjectName,
                      )}
                      <span className="text-emerald-400">/</span>
                      {link.categoryName}
                    </span>
                    <span className="text-[11px] text-slate-500">
                      {lastSyncDate
                        ? `最終同期 ${formatRelativeFromNow(lastSyncDate)}`
                        : '未同期'}
                    </span>
                    {link.lastSyncError ? (
                      <span
                        className="rounded bg-red-50 px-1.5 py-0.5 text-[11px] text-red-700"
                        title={link.lastSyncError}
                      >
                        最終同期エラー
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <Button
                      variant="secondary"
                      onClick={() => handleSyncOne(link)}
                      disabled={isSyncing || isUnlinking || otherBusy}
                      className="!py-1 !text-xs"
                      data-testid="phase-category-sync-now"
                    >
                      {isSyncing ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Spinner className="h-3 w-3" /> 同期中…
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5">
                          <RefreshCw className="h-3 w-3" /> 今すぐ同期
                        </span>
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => handleUnlinkOne(link)}
                      disabled={isSyncing || isUnlinking || otherBusy}
                      className="!py-1 !text-xs text-red-600 hover:text-red-700"
                      data-testid="phase-category-unlink"
                    >
                      {isUnlinking ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Spinner className="h-3 w-3" /> 解除中…
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5">
                          <Unlink className="h-3 w-3" /> 連携を解除
                        </span>
                      )}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="flex items-center">
            <Button
              variant="secondary"
              onClick={() => setDialogOpen(true)}
              disabled={busyKey != null}
              className="!py-1 !text-xs"
              data-testid="phase-category-add"
            >
              <span className="inline-flex items-center gap-1.5">
                <LinkIcon className="h-3 w-3" /> カテゴリを追加
              </span>
            </Button>
          </div>
          {feedback ? (
            <p
              className={
                feedback.kind === 'error'
                  ? 'text-[11px] text-red-600'
                  : 'text-[11px] text-emerald-700'
              }
              data-testid="phase-category-feedback"
            >
              {feedback.message}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="space-y-1" data-testid="phase-category-unlinked">
          <p className="text-[11px] text-slate-500">
            Backlog のカテゴリ配下の課題を、このフェーズ直下の WBS
            タスクとして自動取り込みできます。複数のカテゴリ（異プロジェクトを含む）を紐付けることもできます。
          </p>
          <Button
            variant="secondary"
            onClick={() => setDialogOpen(true)}
            className="!py-1 !text-xs"
            data-testid="phase-category-link-cta"
          >
            <span className="inline-flex items-center gap-1.5">
              <LinkIcon className="h-3 w-3" /> カテゴリと連携
            </span>
          </Button>
          {feedback ? (
            <p
              className={
                feedback.kind === 'error'
                  ? 'text-[11px] text-red-600'
                  : 'text-[11px] text-emerald-700'
              }
              data-testid="phase-category-feedback"
            >
              {feedback.message}
            </p>
          ) : null}
        </div>
      )}
      <PhaseBacklogCategoryLinkDialog
        open={dialogOpen}
        projectId={projectId}
        phaseId={phase.itemId}
        phaseName={phase.name || '(無題)'}
        existingLinkKeys={existingKeys}
        onClose={() => setDialogOpen(false)}
        onLinked={(res) => {
          if (res.ok) {
            setFeedback({
              kind: 'info',
              message: `${res.category.name} と連携しました — 新規 ${res.added.length} 件 / 既存 ${res.unchanged.length} 件${res.truncated ? '（上限到達のため一部省略）' : ''}`,
            });
          }
        }}
      />
    </Section>
  );
}

/**
 * Phase-only section: a list of Backlog **parent-issue** links on this
 * phase. The parent-issue analogue of {@link PhaseBacklogMilestoneSection}.
 *
 * Two states:
 *   - No links → CTA opens {@link PhaseBacklogParentIssueLinkDialog}.
 *   - One or more links → each entry rendered as a row with "今すぐ同期"
 *     + "連携を解除" controls; a "親課題を追加" button appends another.
 */
function PhaseBacklogParentIssueSection({
  projectId,
  phase,
}: {
  projectId: string;
  phase: WbsItemDoc;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{
    kind: 'info' | 'error';
    message: string;
  } | null>(null);
  const links: PhaseBacklogParentIssueLink[] = Array.isArray(
    phase.backlogParentIssueLinks,
  )
    ? phase.backlogParentIssueLinks
    : [];
  const existingKeys = useMemo(
    () =>
      new Set(
        links.map((l) => `${l.backlogProjectKey}:${l.parentIssueKey.toUpperCase()}`),
      ),
    [links],
  );

  const handleSyncOne = async (link: PhaseBacklogParentIssueLink) => {
    const opKey = `sync:${link.backlogProjectKey}:${link.parentIssueId}`;
    setBusyKey(opKey);
    setFeedback(null);
    try {
      const res = await syncBacklogParentIssueNow({
        projectId,
        phaseId: phase.itemId,
        backlogProjectKey: link.backlogProjectKey,
        parentIssueId: link.parentIssueId,
      });
      if (!res.ok) {
        setFeedback({ kind: 'error', message: res.message });
        return;
      }
      setFeedback({
        kind: 'info',
        message: `${link.parentIssueKey} を同期 — 新規 ${res.added.length} / 解除 ${res.removed.length} / 既存 ${res.unchanged.length}`,
      });
    } catch (err) {
      setFeedback({
        kind: 'error',
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setBusyKey(null);
    }
  };

  const handleUnlinkOne = async (link: PhaseBacklogParentIssueLink) => {
    const removeImported = window.confirm(
      `「${link.parentIssueKey}」から取り込んだ子課題（この親課題由来の WBS タスク）も一緒に削除しますか？\n\n[OK] 削除する  [キャンセル] 行は残してリンクのみ解除`,
    );
    const opKey = `unlink:${link.backlogProjectKey}:${link.parentIssueId}`;
    setBusyKey(opKey);
    setFeedback(null);
    try {
      const res = await unlinkPhaseFromBacklogParentIssue({
        projectId,
        phaseId: phase.itemId,
        backlogProjectKey: link.backlogProjectKey,
        parentIssueId: link.parentIssueId,
        removeImportedItems: removeImported,
      });
      if (!res.ok) {
        setFeedback({ kind: 'error', message: res.message });
        return;
      }
      const detail = removeImported
        ? `${res.removedItemCount} 件を削除`
        : `${res.clearedItemCount} 件のリンクを解除`;
      setFeedback({
        kind: 'info',
        message: `${link.parentIssueKey} の連携を解除しました（${detail}）`,
      });
    } catch (err) {
      setFeedback({
        kind: 'error',
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <Section title="Backlog 親課題連携">
      {links.length > 0 ? (
        <div className="space-y-3" data-testid="phase-parent-linked">
          <ul className="space-y-2">
            {links.map((link) => {
              const lastSyncDate = tsToDate(link.lastSyncedAt ?? null);
              const entryKey = `${link.backlogProjectKey}:${link.parentIssueId}`;
              const isSyncing = busyKey === `sync:${entryKey}`;
              const isUnlinking = busyKey === `unlink:${entryKey}`;
              const otherBusy = busyKey != null && !isSyncing && !isUnlinking;
              return (
                <li
                  key={entryKey}
                  className="rounded border border-slate-200 bg-white px-2 py-1.5"
                  data-testid="phase-parent-link-row"
                >
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="inline-flex items-center gap-1 rounded bg-violet-50 px-1.5 py-0.5 font-medium text-violet-700">
                      <LinkIcon className="h-3 w-3" />
                      {formatBacklogProjectLabel(
                        link.backlogProjectKey,
                        link.backlogProjectName,
                      )}
                      <span className="text-violet-400">/</span>
                      {link.parentIssueKey}
                    </span>
                    <span className="text-[11px] text-slate-600">
                      {link.parentIssueSummary}
                    </span>
                    {link.forceSystems && link.forceSystems.length > 0 ? (
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600">
                        領域固定: {link.forceSystems.join(' / ')}
                      </span>
                    ) : null}
                    <span className="text-[11px] text-slate-500">
                      {lastSyncDate
                        ? `最終同期 ${formatRelativeFromNow(lastSyncDate)}`
                        : '未同期'}
                    </span>
                    {link.lastSyncError ? (
                      <span
                        className="rounded bg-red-50 px-1.5 py-0.5 text-[11px] text-red-700"
                        title={link.lastSyncError}
                      >
                        最終同期エラー
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <Button
                      variant="secondary"
                      onClick={() => handleSyncOne(link)}
                      disabled={isSyncing || isUnlinking || otherBusy}
                      className="!py-1 !text-xs"
                      data-testid="phase-parent-sync-now"
                    >
                      {isSyncing ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Spinner className="h-3 w-3" /> 同期中…
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5">
                          <RefreshCw className="h-3 w-3" /> 今すぐ同期
                        </span>
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => handleUnlinkOne(link)}
                      disabled={isSyncing || isUnlinking || otherBusy}
                      className="!py-1 !text-xs text-red-600 hover:text-red-700"
                      data-testid="phase-parent-unlink"
                    >
                      {isUnlinking ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Spinner className="h-3 w-3" /> 解除中…
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5">
                          <Unlink className="h-3 w-3" /> 連携を解除
                        </span>
                      )}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="flex items-center">
            <Button
              variant="secondary"
              onClick={() => setDialogOpen(true)}
              disabled={busyKey != null}
              className="!py-1 !text-xs"
              data-testid="phase-parent-add"
            >
              <span className="inline-flex items-center gap-1.5">
                <LinkIcon className="h-3 w-3" /> 親課題を追加
              </span>
            </Button>
          </div>
          {feedback ? (
            <p
              className={
                feedback.kind === 'error'
                  ? 'text-[11px] text-red-600'
                  : 'text-[11px] text-violet-700'
              }
              data-testid="phase-parent-feedback"
            >
              {feedback.message}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="space-y-1" data-testid="phase-parent-unlinked">
          <p className="text-[11px] text-slate-500">
            Backlog の親課題（例: APG-5639）の子課題を、このフェーズ直下の WBS
            タスクとして自動取り込みできます。子課題が追加されると日次同期で自動反映されます。
          </p>
          <Button
            variant="secondary"
            onClick={() => setDialogOpen(true)}
            className="!py-1 !text-xs"
            data-testid="phase-parent-link-cta"
          >
            <span className="inline-flex items-center gap-1.5">
              <LinkIcon className="h-3 w-3" /> 親課題と連携
            </span>
          </Button>
          {feedback ? (
            <p
              className={
                feedback.kind === 'error'
                  ? 'text-[11px] text-red-600'
                  : 'text-[11px] text-violet-700'
              }
              data-testid="phase-parent-feedback"
            >
              {feedback.message}
            </p>
          ) : null}
        </div>
      )}
      <PhaseBacklogParentIssueLinkDialog
        open={dialogOpen}
        projectId={projectId}
        phaseId={phase.itemId}
        phaseName={phase.name || '(無題)'}
        existingParentKeys={existingKeys}
        onClose={() => setDialogOpen(false)}
        onLinked={(res) => {
          if (res.ok) {
            setFeedback({
              kind: 'info',
              message: `${res.parentIssue.key} と連携しました — 子課題 ${res.totalChildIssues} 件（新規 ${res.added.length} 件 / 既存 ${res.unchanged.length} 件）${res.truncated ? '（上限到達のため一部省略）' : ''}`,
            });
          }
        }}
      />
    </Section>
  );
}

function BacklogSection({
  item,
  allItems,
  backlogCache,
  spaceUrl,
  runSave,
}: {
  item: WbsItemDoc;
  allItems: readonly WbsItemDoc[];
  backlogCache: readonly {
    issueKey: string;
    statusName: string;
    url: string;
    title: string;
  }[];
  spaceUrl: string | null;
  runSave: (op: () => Promise<void>) => Promise<void>;
}) {
  // issueKey → human label of "rows that already link it" so the
  // <datalist> autocomplete option can warn about double-uses. WBS
  // owns this story (cross-row dedupe matters in long projects); the
  // shared component just renders the note as-is.
  const linkedElsewhereLabelByKey = useMemo(() => {
    const m = new Map<string, string>();
    const counts = new Map<string, WbsItemDoc[]>();
    for (const it of allItems) {
      if (it.itemId === item.itemId) continue;
      for (const k of it.backlogIssueKeys) {
        const list = counts.get(k);
        if (list) list.push(it);
        else counts.set(k, [it]);
      }
    }
    for (const [k, linked] of counts) {
      m.set(
        k,
        linked.length === 1
          ? `「${linked[0]!.name || '(無題)'}」に紐付け済み`
          : `${linked.length} 件のタスクに紐付け済み`,
      );
    }
    return m;
  }, [allItems, item.itemId]);

  return (
    <Section title="Backlog 課題">
      <BacklogLinkSection
        issueKeys={item.backlogIssueKeys}
        backlogCache={backlogCache}
        spaceUrl={spaceUrl}
        datalistId={`backlog-cache-${item.itemId}`}
        linkedElsewhereLabelByKey={linkedElsewhereLabelByKey}
        showAggregationNotice
        onAdd={async (key) => {
          if (item.backlogIssueKeys.includes(key)) return;
          await runSave(() =>
            updateWbsItem(getDb(), item.projectId, item.itemId, {
              backlogIssueKeys: [...item.backlogIssueKeys, key],
            }),
          );
        }}
        onRemove={async (key) => {
          await runSave(() =>
            updateWbsItem(getDb(), item.projectId, item.itemId, {
              backlogIssueKeys: item.backlogIssueKeys.filter((k) => k !== key),
            }),
          );
        }}
      />
    </Section>
  );
}
